import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, ArrowRight, Loader2 } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useClerk } from "@clerk/clerk-react";

export default function LockedOutPage() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuthContext();
  const { signOut } = useClerk();
  const leaveOrganization = useMutation(api.users.leaveOrganization);
  
  const [isLeaving, setIsLeaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshAuth();
    setIsRefreshing(false);
    navigate("/dashboard");
  };

  const handleLeaveOrganization = async () => {
    if (!confirm("Are you sure you want to leave this organization? You will not be able to access it again unless re-invited by the owner.")) {
      return;
    }

    try {
      setIsLeaving(true);
      await leaveOrganization();
      toast.success("Successfully left the organization.");
      
      // Force token refresh so the frontend realizes we have no org
      await refreshAuth();
      
      // Send them to onboarding to create their own workspace
      navigate("/onboarding", { replace: true });
    } catch (err: any) {
      toast.error(err.message || "Failed to leave organization.");
      setIsLeaving(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/sign-in");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-8 shadow-xl border border-gray-100">
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 mb-6">
            <Lock className="h-8 w-8 text-orange-600" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-8">
            Your organization's current plan does not support team members. Please ask the owner to upgrade to a Team plan to restore access.
          </p>

          <div className="space-y-4">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || isLeaving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:opacity-50 transition-all"
            >
              {isRefreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              I asked them to upgrade (Refresh)
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm font-medium leading-6">
                <span className="bg-white px-6 text-gray-400">or</span>
              </div>
            </div>

            <button
              onClick={handleLeaveOrganization}
              disabled={isLeaving || isRefreshing}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-all group"
            >
              {isLeaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Leave Organization & Create My Own
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                </>
              )}
            </button>

            <button
              onClick={handleLogout}
              disabled={isLeaving || isRefreshing}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-all"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
